import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import DashboardLayout from './layout/Dashboard';
import { getDashboardReport } from '../api/admin/reports';
import { ACL } from '../config/constants/UogACL/ACL';
import { PageHeader, PageSurface, PageWorkspace } from '../components/layout/Workspace';
import {
  createDashboardReportFilters,
  createDefaultReportFilters,
  serializeReportQuery
} from './reports/reportFilters';
import {
  formatMinorUnits,
  formatQuantity
} from './reports/reportFormatters';
import { DashboardMetricCard } from './dashboard/DashboardMetricCard';
import './Home.scss';

function localizedFormatLocale(locale) {
  return String(locale || 'ro').toLowerCase().startsWith('en') ? 'en' : 'ro';
}

function periodRangeLabel(period, fallback = '—') {
  if (!period) {
    return fallback;
  }

  const from = period.date_from || period.from || period.start;
  const to = period.date_to || period.to || period.end;
  return from && to ? `${from} – ${to}` : fallback;
}

function MoneyDetail({ money, locale }) {
  return (
    <span>
      <FormattedMessage id="pages.dashboard.withVat"/>: {formatMinorUnits(money, 'with_vat', locale)}
    </span>
  );
}

function FinancialMetricCard({ labelId, locale, metric, testId }) {
  const values = metric || {};

  return (
    <DashboardMetricCard
      currentDetail={<MoneyDetail locale={locale} money={values.current}/>}
      currentValue={formatMinorUnits(values.current, 'without_vat', locale)}
      labelId={labelId}
      previousDetail={<MoneyDetail locale={locale} money={values.previous}/>}
      previousValue={formatMinorUnits(values.previous, 'without_vat', locale)}
      testId={testId}
    />
  );
}

function QuantityMetricCard({ labelId, locale, metric, testId }) {
  const values = metric || {};

  return (
    <DashboardMetricCard
      currentValue={formatQuantity(values.current, locale)}
      labelId={labelId}
      previousValue={formatQuantity(values.previous, locale)}
      testId={testId}
    />
  );
}

function organizationHref(uuid) {
  return `/organizations/${encodeURIComponent(uuid)}`;
}

function TopOrganizations({ locale, rows }) {
  const organizations = Array.isArray(rows) ? rows : [];

  return (
    <article
      aria-labelledby="dashboard-top-organizations-title"
      className="dashboard-ranking-card"
      data-testid="dashboard-top-organizations"
    >
      <h3 id="dashboard-top-organizations-title">
        <FormattedMessage id="pages.dashboard.topOrganizations"/>
      </h3>
      {organizations.length === 0 ? (
        <div className="dashboard-ranking-card__empty" data-testid="dashboard-top-organizations-empty">
          <FormattedMessage id="pages.dashboard.noTopOrganizations"/>
        </div>
      ) : (
        <ol className="dashboard-ranking-card__list">
          {organizations.map((organization, index) => (
            <li className="dashboard-ranking-card__row" key={organization.uuid || `${organization.name}-${index}`}>
              <span aria-label={`${index + 1}`} className="dashboard-ranking-card__rank">{index + 1}</span>
              <div className="dashboard-ranking-card__name">
                {organization.uuid ? (
                  <Link to={organizationHref(organization.uuid)}>{organization.name || '—'}</Link>
                ) : (organization.name || '—')}
              </div>
              <dl className="dashboard-ranking-card__values">
                <div>
                  <dt><FormattedMessage id="pages.dashboard.currentPeriod"/></dt>
                  <dd>{formatQuantity(organization.current, locale)}</dd>
                </div>
                <div>
                  <dt><FormattedMessage id="pages.dashboard.previousPeriod"/></dt>
                  <dd>{formatQuantity(organization.previous, locale)}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

function DashboardKpis({ data, locale }) {
  const financial = data.financial || {};
  const productKpis = data.product_kpis || {};
  const activity = data.activity || {};
  const signedPackages = activity.signed_packages || {};

  return (
    <div className="dashboard-kpis" data-testid="dashboard-kpis">
      <section aria-labelledby="dashboard-financial-title" className="dashboard-kpi-group">
        <div className="dashboard-kpi-group__heading">
          <h2 id="dashboard-financial-title"><FormattedMessage id="pages.dashboard.financial"/></h2>
          <span><FormattedMessage id="pages.dashboard.financialHint"/></span>
        </div>
        <div className="dashboard-kpi-grid dashboard-kpi-grid--financial">
          <FinancialMetricCard
            labelId="pages.dashboard.grossSales"
            locale={locale}
            metric={financial.gross_sales}
            testId="dashboard-gross-sales"
          />
          <FinancialMetricCard
            labelId="pages.dashboard.refunds"
            locale={locale}
            metric={financial.refunds}
            testId="dashboard-refunds"
          />
          <FinancialMetricCard
            labelId="pages.dashboard.netSales"
            locale={locale}
            metric={financial.net_sales}
            testId="dashboard-net-sales"
          />
        </div>
      </section>

      <section aria-labelledby="dashboard-product-title" className="dashboard-kpi-group">
        <div className="dashboard-kpi-group__heading">
          <h2 id="dashboard-product-title"><FormattedMessage id="pages.dashboard.kpi"/></h2>
          <span><FormattedMessage id="pages.dashboard.kpiHint"/></span>
        </div>
        <div className="dashboard-kpi-grid dashboard-kpi-grid--products">
          <QuantityMetricCard
            labelId="pages.dashboard.certificateWithAnaf"
            locale={locale}
            metric={productKpis.certificate_with_auth}
            testId="dashboard-certificate-with-anaf"
          />
          <QuantityMetricCard
            labelId="pages.dashboard.certificates"
            locale={locale}
            metric={productKpis.certificates}
            testId="dashboard-certificates"
          />
          <QuantityMetricCard
            labelId="pages.dashboard.qualifiedSignatures"
            locale={locale}
            metric={productKpis.qualified_signatures}
            testId="dashboard-qualified-signatures"
          />
          <QuantityMetricCard
            labelId="pages.dashboard.simpleSignatures"
            locale={locale}
            metric={productKpis.simple_signatures}
            testId="dashboard-simple-signatures"
          />
        </div>
      </section>

      <section aria-labelledby="dashboard-activity-title" className="dashboard-kpi-group">
        <div className="dashboard-kpi-group__heading">
          <h2 id="dashboard-activity-title"><FormattedMessage id="pages.dashboard.activity"/></h2>
          <span><FormattedMessage id="pages.dashboard.activityHint"/></span>
        </div>
        <div className="dashboard-activity-grid">
          <QuantityMetricCard
            labelId="pages.dashboard.simpleSignedPackages"
            locale={locale}
            metric={signedPackages.simple}
            testId="dashboard-simple-signed-packages"
          />
          <QuantityMetricCard
            labelId="pages.dashboard.qualifiedSignedPackages"
            locale={locale}
            metric={signedPackages.qualified}
            testId="dashboard-qualified-signed-packages"
          />
          <TopOrganizations locale={locale} rows={activity.top_organizations}/>
        </div>
      </section>
    </div>
  );
}

function DashboardLoading() {
  return (
    <div
      aria-busy="true"
      className="dashboard-kpi-grid dashboard-kpi-grid--loading"
      data-testid="dashboard-loading"
    >
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(item => <span key={item}/>) }
    </div>
  );
}

function HomeComponent({ canViewReports, locale, location }) {
  const filters = useMemo(() => createDashboardReportFilters(), []);
  const reportLinkFilters = useMemo(() => createDefaultReportFilters(), []);
  const reportsHref = useMemo(
    () => `/reports${serializeReportQuery(reportLinkFilters, 'summary')}`,
    [reportLinkFilters]
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(Boolean(canViewReports));
  const [refreshing, setRefreshing] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const activeRequestRef = useRef(0);
  const dataRef = useRef(null);
  const formatLocale = localizedFormatLocale(locale);

  useEffect(() => {
    if (!canViewReports) {
      setLoading(false);
      setRefreshing(false);
      setError(false);
      dataRef.current = null;
      setData(null);
      return undefined;
    }

    const controller = new AbortController();
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;
    setError(false);
    setLoading(!dataRef.current);
    setRefreshing(Boolean(dataRef.current));

    getDashboardReport(filters, controller.signal)
      .then(response => {
        if (controller.signal.aborted || requestId !== activeRequestRef.current) {
          return;
        }
        const nextData = response && response.data ? response.data : null;
        dataRef.current = nextData;
        setData(nextData);
      })
      .catch(() => {
        if (!controller.signal.aborted && requestId === activeRequestRef.current) {
          setError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && requestId === activeRequestRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => controller.abort();
  }, [canViewReports, filters, requestVersion]);

  const refresh = () => setRequestVersion(version => version + 1);
  const actions = canViewReports ? (
    <>
      <button
        aria-busy={refreshing}
        className="btn btn-outline-primary btn-sm dashboard-overview__refresh"
        disabled={loading || refreshing}
        onClick={refresh}
        type="button"
      >
        <i aria-hidden="true" className="fas fa-sync-alt"/>
        <FormattedMessage id={refreshing ? 'pages.dashboard.refreshing' : 'pages.dashboard.refresh'}/>
      </button>
      <Link className="btn btn-primary btn-sm" to={reportsHref}>
        <FormattedMessage id="pages.dashboard.openReports"/>
      </Link>
    </>
  ) : null;

  let content;
  if (!canViewReports) {
    content = (
      <div className="dashboard-overview__state" data-testid="dashboard-no-access">
        <i aria-hidden="true" className="fas fa-lock"/>
        <strong><FormattedMessage id="pages.dashboard.noAccessTitle"/></strong>
        <span><FormattedMessage id="pages.dashboard.noAccess"/></span>
      </div>
    );
  } else if (loading) {
    content = <DashboardLoading/>;
  } else if (error && !data) {
    content = (
      <div className="dashboard-overview__state dashboard-overview__state--error" role="alert">
        <i aria-hidden="true" className="fas fa-exclamation-circle"/>
        <strong><FormattedMessage id="pages.dashboard.errorTitle"/></strong>
        <span><FormattedMessage id="pages.dashboard.error"/></span>
        <button className="btn btn-outline-primary btn-sm" onClick={refresh} type="button">
          <FormattedMessage id="pages.dashboard.retry"/>
        </button>
      </div>
    );
  } else if (!data || !data.financial || !data.product_kpis || !data.activity) {
    content = (
      <div className="dashboard-overview__state" data-testid="dashboard-empty">
        <i aria-hidden="true" className="fas fa-inbox"/>
        <strong><FormattedMessage id="pages.dashboard.emptyTitle"/></strong>
        <span><FormattedMessage id="pages.dashboard.empty"/></span>
      </div>
    );
  } else {
    content = (
      <>
        {error && (
          <div className="dashboard-overview__refresh-error" role="alert">
            <FormattedMessage id="pages.dashboard.refreshError"/>
          </div>
        )}
        <DashboardKpis data={data} locale={formatLocale}/>
      </>
    );
  }

  return (
    <DashboardLayout location={location} className="dashboard" workspace={true}>
      <PageWorkspace className="dashboard-overview">
        <PageHeader actions={actions} title={<FormattedMessage id="pages.dashboard.title"/>}/>
        {canViewReports && (
          <div className="dashboard-overview__context">
            <span><FormattedMessage id="components.pages.reports.filters.period.ytd"/></span>
            <span>
              <FormattedMessage id="pages.dashboard.currentPeriod"/>: {' '}
              {periodRangeLabel(
                data && data.meta ? data.meta.period : null,
                `${filters.dateFrom} – ${filters.dateTo}`
              )}
            </span>
            <span>
              <FormattedMessage id="pages.dashboard.previousPeriod"/>: {' '}
              {data && data.meta && data.meta.comparison_period
                ? periodRangeLabel(data.meta.comparison_period)
                : <FormattedMessage id="pages.dashboard.comparedPrevious"/>}
            </span>
            {data && data.meta && data.meta.indexed_at && (
              <span><FormattedMessage id="pages.dashboard.indexedAt"/>: {data.meta.indexed_at}</span>
            )}
          </div>
        )}
        <PageSurface className="dashboard-overview__surface">{content}</PageSurface>
      </PageWorkspace>
    </DashboardLayout>
  );
}

HomeComponent.defaultProps = {
  canViewReports: false,
  locale: 'ro',
  location: null
};

const mapStateToProps = state => ({
  canViewReports: ACL.CLIENT.REPORTS.LIST.hasAccess(),
  locale: state && state.locale ? state.locale.lang : 'ro'
});

const Home = connect(mapStateToProps, null)(HomeComponent);

export { DashboardKpis, Home, HomeComponent, TopOrganizations, periodRangeLabel };
